import { throttle } from 'lodash';
import React, { useCallback, useEffect } from 'react';
import { DocData, DocMetaData } from 'src/docs/types';
import { useDocsContext, Offset } from './DocsContext';

const OFFSET_ACTIVE_ITEM = 64;

function getOffsets(meta: DocMetaData): Offset[] {
  if (!meta.headings) {
    return [];
  }

  return meta.headings
    .map(({ slug }) => {
      const element = document.getElementById(slug);
      return element
        ? {
            slug,
            offset: element.offsetTop,
          }
        : null;
    })
    .filter((item) => item) as Offset[];
}

interface Props {
  docData: DocData;
  children: React.ReactNode;
}

export default function DocsScrollManager({ docData, children }: Props): React.ReactElement {
  const { setActiveIds, offsets, setOffsets } = useDocsContext();

  useEffect(() => {
    const newOffsets = getOffsets(docData.meta);
    setOffsets(newOffsets);
  }, [docData.meta]);

  const onScroll = useCallback(
    throttle(() => {
      const item = offsets.find((current, i) => {
        if (!current) {
          return false;
        }

        const next = offsets[i + 1];

        return next ? window.scrollY + OFFSET_ACTIVE_ITEM >= current.offset && window.scrollY + OFFSET_ACTIVE_ITEM < next.offset : window.scrollY + OFFSET_ACTIVE_ITEM >= current.offset;
      });

      const newActiveItemIds = item ? [item.slug] : [];

      setActiveIds(newActiveItemIds);
    }, 100),
    [offsets, setActiveIds],
  );

  useEffect(() => {
    window.addEventListener('scroll', onScroll);

    return (): void => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [onScroll]);

  return <>{children}</>;
}
